import cn from 'classnames';

import { LogisticFee } from '@zooplus/checkout-state';
import {
  ModalWrapper,
  ModalBody,
  ModalFooter,
  ModalSecondaryButton,
  FlatBox,
  ShadowBox,
  Alert,
  H3,
  P1,
  P2,
} from '@zooplus/ui-components';

import CartLogisticFeesMessage from '@components/cart/cart-logistics-fees/cart-logistic-fees-message';
import { useTranslation } from '@hooks/use-translations';
import { isArrayNotEmpty } from '@utilities/utils';

import styles from './cart-logistics-fees.module.scss';

type CartLogisticsFeesModalProps = {
  isModalOpen: boolean;
  callback: Function;
  logisticFee: LogisticFee;
};

const CartLogisticsFeesModal = ({
  isModalOpen,
  callback,
  logisticFee,
}: CartLogisticsFeesModalProps) => {
  const { t } = useTranslation();
  const {
    articleDetails,
    formattedTotalWeight,
    totalWeightUnit,
    feeDetails,
    formattedTotalLogisticsFee,
  } = logisticFee;

  return (
    <ModalWrapper
      isOpen={isModalOpen}
      onClose={() => callback(false)}
      closeBtnAriaLabel={t('cart.button.close')}
      heading={t('cart.summary.logisticFeesModal.title')}
      headingId={'logisticFeesModal'}>
      <ModalBody>
        <ShadowBox>
          <div className={styles.logisticFeeModalWrapper}>
            <Alert data-testid="info-box-text" className={styles.infoBox}>
              {feeDetails && formattedTotalWeight && (
                <CartLogisticFeesMessage
                  feeDetails={feeDetails}
                  formattedTotalWeight={formattedTotalWeight}
                />
              )}
            </Alert>
            <div className={styles.logisticFeeCartTable}>
              <div className={styles.cartTableHeader}>
                <P2 bold>{t('cart.summary.logisticFeesModal.product')}</P2>
                <P2 bold>{t('cart.summary.logisticFeesModal.weight')}</P2>
              </div>
              {articleDetails &&
                isArrayNotEmpty(articleDetails) &&
                articleDetails.map(
                  (product, index) =>
                    product.weight > 0 && (
                      <div
                        id={`${product.id}`}
                        key={index}
                        className={`${cn(
                          styles.productInfoWrapper,
                          index !== articleDetails.length - 1 && styles.applyBorder,
                        )}`}>
                        <div className={styles.productInfo}>
                          <div className={styles.productImgWrapper}>
                            <img
                              loading="lazy"
                              src={product.fullPictureUrl}
                              alt={product.productName}
                            />
                          </div>
                          <div className={styles.productNameWrapper}>
                            <P1 bold>{product.productName}</P1>
                            <P2>{product.variantText}</P2>
                          </div>
                        </div>
                        <div className={styles.overweightProductInfo}>
                          <P1 data-testid="product-weight">{`${product.formattedWeight} ${product.unit}`}</P1>
                          <P1 data-testid="product-quantity">{`${product.quantity}x`}</P1>
                          <P1 data-testid="product-total-weight">{`${product.formattedSubtotalWeight} ${product.unit}`}</P1>
                        </div>
                      </div>
                    ),
                )}
              <div className={styles.totalWeightInfo}>
                <P1 bold>{t('cart.summary.logisticFeesModal.totalWeight')}</P1>
                <P1
                  bold
                  data-testid="cart-total-weight">{`${formattedTotalWeight} ${totalWeightUnit}`}</P1>
              </div>
              <div className={styles.overweightFee}>
                <div className={styles.overweightFeeTitle}>
                  <P1 bold>{t('cart.summary.logisticFeesModal.serviceCharge')}</P1>
                  <P1>{t('cart.summary.logisticFeesModal.perAdditionalParcel')}</P1>
                </div>
                <P1
                  data-testid="overweight-fee-factor"
                  className={styles.overweightFeeFactor}>{`${feeDetails?.feeFactor}x`}</P1>
                <P1 data-testid="overweight-fee-modal" className={styles.overweightFeeValue}>
                  {feeDetails?.formattedFeeValue}
                </P1>
              </div>
              <FlatBox rounded className={styles.grandTotalFee}>
                <H3>{t('cart.summary.logisticFeesModal.totalServiceCharge')}</H3>
                <H3 data-testid="overweight-total-fee">{formattedTotalLogisticsFee}</H3>
              </FlatBox>
            </div>
          </div>
        </ShadowBox>
      </ModalBody>
      <ModalFooter className={styles.modalFooter}>
        <ModalSecondaryButton onClick={() => callback(false)}>
          {t('cart.button.close')}
        </ModalSecondaryButton>
      </ModalFooter>
    </ModalWrapper>
  );
};

export default CartLogisticsFeesModal;
